// frontend/src/hooks/useMarketplaceSummary.js
// Fetches a lightweight marketplace used-copy summary for a single game.
// In-flight requests are cancelled when the game changes or the component unmounts.

import { useEffect, useRef, useState } from 'react';
import { API_BASE } from '../config/api';
import { authService } from '../services/auth';
import { httpRequest } from '../utils/httpClient';
import { detectRegion } from '../utils/regionDetection';

// Shared in-flight region detection so multiple tiles trigger only one ipapi call
let regionPromise = null;

async function getDetectedRegion() {
  if (regionPromise) return regionPromise;
  regionPromise = detectRegion().finally(() => {
    regionPromise = null;
  });
  return regionPromise;
}

function formatCurrencyPrice(amount, currencyCode) {
  const code = (currencyCode || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
    }).format(amount ?? 0);
  } catch {
    return `${code}${Number(amount ?? 0).toFixed(2)}`;
  }
}

function buildSummary(usedCopy) {
  if (!usedCopy) return null;
  const listingCount = usedCopy.listing_count ?? 0;
  if (listingCount <= 0 || usedCopy.median_price === null || usedCopy.median_price === undefined) return null;

  const formattedPrice = formatCurrencyPrice(usedCopy.median_price, usedCopy.currency);
  const copyWord = listingCount === 1 ? 'copy' : 'copies';
  return {
    listingCount,
    medianPrice: usedCopy.median_price,
    currency: usedCopy.currency,
    formattedPrice,
    label: `${listingCount} ${copyWord}: ~${formattedPrice}`,
  };
}

export function useMarketplaceSummary(gameId, enabled = true) {
  const [summary, setSummary] = useState(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!gameId || !enabled) {
      setSummary((prev) => (prev === null ? prev : null));
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    async function fetchSummary() {
      try {
        const region = await getDetectedRegion();
        const params = new URLSearchParams({ game_id: String(gameId) });
        if (region?.country) params.set('user_country', region.country);
        if (region?.currency) params.set('base_currency', region.currency);

        const res = await httpRequest(
          `${API_BASE}/marketplace/search?${params.toString()}`,
          {
            method: 'GET',
            headers: authService.getAuthHeaders(),
          }
        );

        if (controller.signal.aborted) return;

        if (!res.ok) {
          const text = await res.text().catch(() => 'Unknown error');
          throw new Error(`Marketplace summary failed: ${res.status} ${text}`);
        }

        const data = await res.json();
        if (controller.signal.aborted) return;

        const result = buildSummary(data.used_copy);
        setSummary((prev) => (prev === result ? prev : result));
      } catch (err) {
        if (controller.signal.aborted) return;
        setSummary((prev) => (prev === null ? prev : null));
      }
    }

    fetchSummary();

    return () => {
      controller.abort();
    };
  }, [gameId, enabled]);

  return { summary };
}
